import React, { useEffect, useState, useRef } from 'react';
import { 
  TextStyle, 
  createFrontTemplate, 
  createBackTemplate,
  getAddressHtml,
  CARD_WIDTH_PX,
  CARD_HEIGHT_PX,
  SAFE_WIDTH_PX,
  SAFE_HEIGHT_PX,
  BLEED_PX
} from '../../lib/templates';
import { useUser } from '../../hooks/useUser';
import { TemplatePreviewComponent } from './TemplatePreviewComponent';
import { TemplateDebugInfo } from './TemplateDebugInfo';
import { debug, error as logError } from '../../lib/logger';

// Import the actual logo
import cardibleLogo from '../../../resources/Cardible_Logo.png';

interface TemplatePreviewProps {
  side: 'front' | 'back';
  cardImage?: string;
  message?: string;
  occasion?: string;
  recipientName?: string;
  recipientAddress?: {
    address_line1?: string;
    address_line2?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
  signatureUrl?: string | null;
  textStyle?: TextStyle;
  className?: string;
  showDebug?: boolean;
  id?: string;
}

/**
 * Component that handles template generation and preview
 */
export function TemplatePreview({
  side,
  cardImage,
  message,
  occasion,
  recipientName,
  recipientAddress,
  signatureUrl,
  textStyle,
  className = '',
  showDebug = false,
  id,
}: TemplatePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isAdmin, isLoading: isUserLoading } = useUser();
  const [logoDataUrl, setLogoDataUrl] = useState<string>('');

  // Load and convert the logo to a data URL once on component mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        // Create an in-memory image from the imported logo
        const img = new Image();
        img.src = cardibleLogo;
        
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = () => {
            logError('Failed to load logo');
            resolve(null);
          };
        });
        
        // Create a canvas to convert the image to a data URL
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          setLogoDataUrl(canvas.toDataURL('image/png'));
        }
      } catch (error) {
        logError('Error loading logo:', error);
      }
    };
    
    loadLogo();
  }, []);

  // Generate the HTML template
  useEffect(() => {
    const generateTemplate = async () => {
      setIsLoading(true);
      try {
        // Create debugging info
        const dimensions = {
          fullBleed: {
            width: CARD_WIDTH_PX,
            height: CARD_HEIGHT_PX,
          },
          safeArea: {
            width: SAFE_WIDTH_PX,
            height: SAFE_HEIGHT_PX,
          },
          bleed: BLEED_PX,
        };
        
        debug(`Generating ${side} preview, id=${id}`);
        
        // Handle front side - simple image display
        if (side === 'front' && cardImage) {
          debug(`Front side with image: ${cardImage.substring(0, 50)}...`);
          setDebugInfo({
            side: 'front',
            imageUrl: cardImage,
            dimensions
          });
          
          // For front side, if it's a valid URL, we can use it directly
          if (isValidImageUrl(cardImage)) {
            debug(`Valid image URL detected, using directly`);
            await checkImageValidity(cardImage);
            setPreviewUrl(cardImage);
            setIsLoading(false);
            return;
          } else {
            // If not a valid URL or data URL too large, create a simple SVG preview instead
            debug(`Not a valid image URL, creating SVG preview`);
            const frontSvg = createFrontSvgPreview(cardImage);
            setPreviewUrl(svgToDataURL(frontSvg));
            setIsLoading(false);
            return;
          }
        } 
        
        // Handle back side - we'll use a more direct SVG rendering approach
        else if (side === 'back' && message) {
          const formattedMessage = recipientName 
            ? `Dear ${recipientName},\n\n${message}` 
            : message;
            
          setDebugInfo({
            side: 'back',
            message: formattedMessage,
            textStyle,
            dimensions
          });
          
          // Generate address HTML
          const addressHtml = recipientAddress 
            ? getAddressHtml(
                recipientName || '',
                recipientAddress.address_line1,
                recipientAddress.address_line2,
                recipientAddress.address_city,
                recipientAddress.address_state,
                recipientAddress.address_zip
              )
            : recipientName 
              ? getAddressHtml(recipientName) 
              : '';

          // Create SVG representation of back side
          const backSvg = createBackSvgPreview(
            formattedMessage,
            textStyle,
            signatureUrl || undefined,
            addressHtml
          );
          
          // Use a safer method to convert SVG to data URL
          setPreviewUrl(svgToDataURL(backSvg));
          setIsLoading(false);
          return;
        }
        
        // If we don't have sufficient data, show a placeholder
        setPreviewUrl(generateFallbackPreview(side));
        
      } catch (error) {
        logError('Error generating preview:', error);
        setDebugInfo({ error: error instanceof Error ? error.message : String(error) });
        
        // Set a fallback preview in case of error
        setPreviewUrl(generateFallbackPreview(side));
      } finally {
        setIsLoading(false);
      }
    };

    generateTemplate();
  }, [side, cardImage, message, occasion, recipientName, signatureUrl, textStyle, recipientAddress, logoDataUrl]);

  // Convert SVG string to data URL safely
  const svgToDataURL = (svgString: string): string => {
    try {
      // Create a Blob from the SVG string
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      return URL.createObjectURL(blob);
    } catch (e) {
      logError('Error converting SVG to data URL:', e);
      return generateFallbackPreview(side);
    }
  };

  // Check if a URL is likely to be a valid image
  const isValidImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    // Accept data URLs that start with "data:image/"
    if (url.startsWith('data:image/')) {
      return true;
    }
    
    // Accept blob URLs
    if (url.startsWith('blob:')) {
      return true;
    }
    
    // Accept relative URLs (local files)
    if (url.startsWith('/')) {
      return true;
    }
    
    // Check for common image extensions or http/https URLs
    return (
      url.startsWith('http') &&
      (url.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) !== null || 
       url.includes('unsplash.com') || 
       url.includes('cloudinary.com') ||
       url.includes('supabase.co') ||
       url.includes('storage'))
    );
  };
  
  // Check if an image URL is valid by attempting to load it
  const checkImageValidity = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
      
      // Set a timeout in case the image takes too long to load
      setTimeout(() => resolve(false), 3000);
    });
  };
  
  // Create an SVG representation of the back side
  const createBackSvgPreview = (
    messageText: string, 
    textStyle?: TextStyle,
    signatureUrl?: string,
    addressHtml?: string
  ): string => {
    const font = textStyle?.font || 'Arial, sans-serif';
    const fontSize = parseInt((textStyle?.fontSize || '14px').replace('px', ''));
    const alignment = textStyle?.alignment || 'left';
    const color = textStyle?.color || '#000000';
    
    // Get the text anchor position based on alignment
    const textAnchor = alignment === 'center' ? 'middle' : alignment === 'right' ? 'end' : 'start';
    
    // Set scale factors for our SVG (625x425 pixels represents 6.25"x4.25")
    const SCALE = 100; // 100 pixels per inch
    
    // Card size constants from the specification image
    const BLEED_WIDTH = 6.25 * SCALE; // 6.25" x 100 = 625px
    const BLEED_HEIGHT = 4.25 * SCALE; // 4.25" x 100 = 425px
    const TRIM_WIDTH = 6.0 * SCALE; // 6" x 100 = 600px
    const TRIM_HEIGHT = 4.0 * SCALE; // 4" x 100 = 400px
    const SAFE_WIDTH = 5.875 * SCALE; // 5.875" x 100 = 587.5px
    const SAFE_HEIGHT = 3.875 * SCALE; // 3.875" x 100 = 387.5px
    const BLEED_MARGIN = 0.125 * SCALE; // 0.125" x 100 = 12.5px
    
    // Ink-free area dimensions and positioning per specifications
    const INK_FREE_WIDTH = 3.2835 * SCALE; // 3.2835" x 100 = 328.35px
    const INK_FREE_HEIGHT = 2.375 * SCALE; // 2.375" x 100 = 237.5px
    const INK_FREE_RIGHT_MARGIN = 0.275 * SCALE; // 0.275" from right edge
    const INK_FREE_BOTTOM_MARGIN = 0.25 * SCALE; // 0.25" from bottom edge
    
    // Calculate the exact position for the ink-free area
    const INK_FREE_X = BLEED_WIDTH - INK_FREE_WIDTH - INK_FREE_RIGHT_MARGIN;
    const INK_FREE_Y = BLEED_HEIGHT - INK_FREE_HEIGHT - INK_FREE_BOTTOM_MARGIN;
    
    // Logo positioning - centered in the no-ink area horizontally
    // and vertically between the top of the card and the top of the no-ink area
    const LOGO_WIDTH = 120;
    const LOGO_HEIGHT = 60;
    const LOGO_X = INK_FREE_X + (INK_FREE_WIDTH / 2) - (LOGO_WIDTH / 2); // Center above no-ink area
    const LOGO_Y = (INK_FREE_Y / 2) - (LOGO_HEIGHT / 2); // Center between top of card and top of no-ink area
    
    // Message area padding
    const PADDING = 20;
    
    // Message area dimensions
    // - From left edge to no-ink area with padding
    const MESSAGE_X = BLEED_MARGIN + PADDING; // Left margin of message with padding
    const MESSAGE_Y = BLEED_MARGIN + PADDING; // Top margin of message with padding
    const MESSAGE_WIDTH = INK_FREE_X - MESSAGE_X - PADDING; // Width from left edge to no-ink area minus padding
    
    // Calculate x position for message based on alignment
    const getXPosition = () => {
      switch(alignment) {
        case 'center': return MESSAGE_X + MESSAGE_WIDTH / 2;
        case 'right': return MESSAGE_X + MESSAGE_WIDTH - 10;
        default: return MESSAGE_X; // Default left alignment
      }
    };
    
    const xPosition = getXPosition();
    
    // Get font-specific parameters for line height and spacing
    const getFontParameters = () => {
      // Check for the four specific fonts offered in the UI
      if (font.includes('Brush Script') || font.includes('Brush Script MT')) {
        // Brush Script MT - a flowing script font
        return {
          lineHeightMultiplier: 1.5, // Slightly less line height for readability
          wordSpacing: 1.15  // More reasonable word spacing
        };
      } 
      else if (font.includes('Times New Roman') || font.includes('Times')) {
        // Times New Roman - a classic serif font
        return {
          lineHeightMultiplier: 1.5, // Serifs need a bit more line height
          wordSpacing: 1.0 // Standard word spacing
        };
      }
      else if (font.includes('Courier New') || font.includes('Courier') || font.includes('monospace')) {
        // Courier New - a monospace font
        return {
          lineHeightMultiplier: 1.35, // Can be more compact
          wordSpacing: 1.1 // Slightly more word spacing for readability
        };
      }
      else {
        // Arial (default) - a sans-serif font
        return {
          lineHeightMultiplier: 1.4, // Standard line height
          wordSpacing: 1.02 // Standard word spacing
        };
      }
    };
    
    // Use browser's text measurement via a temporary HTML element
    // This gives much more accurate width calculations than our estimations
    const measureTextWithDOM = (text: string, fontSizeToUse: number): number => {
      // Create a temporary span element with the same styling
      const tempSpan = document.createElement('span');
      tempSpan.style.font = `${fontSizeToUse}px ${font}`;
      tempSpan.style.position = 'absolute';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.textContent = text;
      
      // Add to DOM temporarily for measurement
      document.body.appendChild(tempSpan);
      const width = tempSpan.getBoundingClientRect().width;
      document.body.removeChild(tempSpan);
      
      return width;
    };
    
    // Maximum height for the message area in pixels
    // Increase the max height to better utilize space 
    const MAX_MESSAGE_HEIGHT = 250;
    
    // Start with the original font size 
    let effectiveFontSize = fontSize;
    
    // For Brush Script, start slightly larger to avoid unnecessary scaling
    if (font.includes('Brush Script')) {
      effectiveFontSize = Math.max(fontSize, 16);
    }
    
    // This function wraps text using the browser's accurate text measurement
    const wrapText = (text: string, fontSizeToUse: number): string[] => {
      const result: string[] = [];
      const fontParams = getFontParameters();
      const lineHeight = fontSizeToUse * fontParams.lineHeightMultiplier;
      
      // Process newlines in the original text
      const paragraphs = text.split('\n');
      for (const paragraph of paragraphs) {
        if (paragraph === '') {
          result.push('');
          continue;
        }
        
        // Split paragraph into words
        const words = paragraph.split(/\s+/);
        let currentLine = '';
        
        for (const word of words) {
          // Check if adding this word exceeds the line width
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          
          // Use actual browser measurement for accurate width
          const testLineWidth = measureTextWithDOM(testLine, fontSizeToUse);
          
          // Use almost the full width available
          // Higher percentage = more text per line
          const widthPercentage = font.includes('Brush Script') ? 0.98 : 0.99;
          const effectiveMaxWidth = MESSAGE_WIDTH * widthPercentage;
          
          if (testLineWidth > effectiveMaxWidth && currentLine !== '') {
            result.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        
        // Add the last line from this paragraph
        if (currentLine) {
          result.push(currentLine);
        }
      }
      
      return result;
    };
    
    // Try to find the optimal font size that fills the available space
    let lines: string[] = [];
    let effectiveLineHeight: number;
    let messageTooLong = false;
    
    // Wrap at the initial font size first
    const fontParams = getFontParameters();
    effectiveLineHeight = effectiveFontSize * fontParams.lineHeightMultiplier;
    lines = wrapText(messageText, effectiveFontSize);
    
    // Calculate total height
    let totalHeight = lines.length * effectiveLineHeight;
    
    // If too big, scale down
    const MIN_FONT_SIZE = 13;
    while (totalHeight > MAX_MESSAGE_HEIGHT && effectiveFontSize > MIN_FONT_SIZE) {
      effectiveFontSize -= 1;
      effectiveLineHeight = effectiveFontSize * fontParams.lineHeightMultiplier;
      lines = wrapText(messageText, effectiveFontSize);
      totalHeight = lines.length * effectiveLineHeight;
      messageTooLong = true;
    }
    
    // Special handling for Brush Script - ensure minimum readability
    if (font.includes('Brush Script') && effectiveFontSize < 15) {
      effectiveFontSize = 15;
      effectiveLineHeight = effectiveFontSize * fontParams.lineHeightMultiplier;
      lines = wrapText(messageText, effectiveFontSize);
      totalHeight = lines.length * effectiveLineHeight;
    }
    
    // Calculate actual message box height based on text content
    // Use the actual text height plus some padding
    const actualMessageHeight = Math.max(
      totalHeight + effectiveLineHeight * 0.5, // Add half a line for bottom padding
      100 // Minimum height
    );
    
    // Constrain to max height if needed
    const approximateHeight = Math.min(actualMessageHeight, MAX_MESSAGE_HEIGHT);
    
    // Position signature much closer to the end of the message text
    const SIGNATURE_HEIGHT = 50;
    const SIGNATURE_PADDING = -25; // Negative padding to overlap with message area
    const SIGNATURE_Y = MESSAGE_Y + approximateHeight + SIGNATURE_PADDING;
    
    // Process message text with word wrapping, safely escaping HTML entities
    const messageLines = lines.map((line, i) => {
      // Escape special characters to prevent XML parsing issues
      const cleanLine = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
      
      return `<tspan x="${xPosition}" dy="${i === 0 ? '0' : `${effectiveLineHeight}px`}">${cleanLine}</tspan>`;
    }).join('');

    // Format recipient address if available
    const formattedAddress = recipientName && recipientAddress ? 
      `<tspan x="${INK_FREE_X + 20}" dy="0">${recipientName}</tspan>
       <tspan x="${INK_FREE_X + 20}" dy="16">${recipientAddress.address_line1 || ''}</tspan>
       ${recipientAddress.address_line2 ? `<tspan x="${INK_FREE_X + 20}" dy="16">${recipientAddress.address_line2}</tspan>` : ''}
       <tspan x="${INK_FREE_X + 20}" dy="16">${recipientAddress.address_city || ''}, ${recipientAddress.address_state || ''} ${recipientAddress.address_zip || ''}</tspan>`
      : recipientName ? 
        `<tspan x="${INK_FREE_X + 20}" dy="0">${recipientName}</tspan>` 
        : '';
    
    // Match the template specifications
    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
      <svg xmlns="http://www.w3.org/2000/svg" width="${BLEED_WIDTH}" height="${BLEED_HEIGHT}" viewBox="0 0 ${BLEED_WIDTH} ${BLEED_HEIGHT}" id="${id || `cardible-${side}-preview-svg`}">
        <!-- White background -->
        <rect width="${BLEED_WIDTH}" height="${BLEED_HEIGHT}" fill="white"/>
        
        <!-- Cardible Logo -->
        ${logoDataUrl ? 
          `<image x="${LOGO_X}" y="${LOGO_Y}" width="${LOGO_WIDTH}" height="${LOGO_HEIGHT}" 
          href="${logoDataUrl}" preserveAspectRatio="xMidYMid meet" image-rendering="optimizeQuality"/>` : 
          `<text x="${LOGO_X + LOGO_WIDTH/2}" y="${LOGO_Y + LOGO_HEIGHT/2}" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#000000" text-anchor="middle" dominant-baseline="middle">Cardible</text>`
        }
        
        <!-- Postage indicia -->
        <rect x="${INK_FREE_X + INK_FREE_WIDTH - 80}" y="${INK_FREE_Y + 20}" width="70" height="70" 
          fill="none" stroke="#000000" stroke-width="1"/>
        <text x="${INK_FREE_X + INK_FREE_WIDTH - 45}" y="${INK_FREE_Y + 50}" 
          font-family="Arial, sans-serif" font-size="10" text-anchor="middle">
          POSTAGE
          <tspan x="${INK_FREE_X + INK_FREE_WIDTH - 45}" dy="15">INDICIA</tspan>
        </text>
        
        <!-- Mail barcode (placeholder) -->
        <rect x="${INK_FREE_X + 20}" y="${INK_FREE_Y + 100}" width="${INK_FREE_WIDTH - 40}" height="10" 
          fill="#000000" opacity="0.7"/>
        
        <!-- Recipient address area -->
        <text x="${INK_FREE_X + 20}" y="${INK_FREE_Y + 130}" 
          font-family="Arial, sans-serif" font-size="12" fill="#333333">
          ${formattedAddress || `RECIPIENT NAME
          <tspan x="${INK_FREE_X + 20}" dy="16">AND FULL ADDRESS</tspan>
          <tspan x="${INK_FREE_X + 20}" dy="16">WILL BE PRINTED</tspan>
          <tspan x="${INK_FREE_X + 20}" dy="16">IN THIS SPACE</tspan>`}
        </text>
        
        ${messageTooLong ? 
          `<!-- Warning for scaled-down message -->
          <rect x="${MESSAGE_X}" y="${MESSAGE_Y}" width="${MESSAGE_WIDTH}" height="16" 
            fill="#ffc107" opacity="0.3" />
          <text x="${MESSAGE_X + 5}" y="${MESSAGE_Y + 12}" 
            font-family="Arial, sans-serif" font-size="10" fill="#d32f2f">
            Message scaled down to fit
          </text>` : ''
        }
        
        <!-- Message text with improved word wrapping -->
        <text x="${xPosition}" y="${MESSAGE_Y + effectiveFontSize}" 
          font-family="${font}" font-size="${effectiveFontSize}px" fill="${color}" text-anchor="${textAnchor}">
          ${messageLines}
        </text>
        
        <!-- Signature image -->
        ${signatureUrl ? 
          `<image x="${MESSAGE_X + 10}" y="${SIGNATURE_Y + 5}" width="${MESSAGE_WIDTH - 20}" height="${SIGNATURE_HEIGHT - 10}" 
          href="${signatureUrl}" preserveAspectRatio="xMidYMid meet"/>` : 
          ''}
      </svg>
    `;
  };

  // Create an SVG representation of the front side
  const createFrontSvgPreview = (imageUrl: string): string => {
    // Set scale factors for our SVG (625x425 pixels represents 6.25"x4.25")
    const SCALE = 100; // 100 pixels per inch
    const BLEED_WIDTH = 6.25 * SCALE; // 6.25" x 100 = 625px
    const BLEED_HEIGHT = 4.25 * SCALE; // 4.25" x 100 = 425px
    
    // Create a basic SVG with the image embedded - optimized for print quality
    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
      <svg xmlns="http://www.w3.org/2000/svg" width="${BLEED_WIDTH}" height="${BLEED_HEIGHT}" viewBox="0 0 ${BLEED_WIDTH} ${BLEED_HEIGHT}" id="${id || `cardible-${side}-preview-svg`}">
        <!-- White background -->
        <rect width="${BLEED_WIDTH}" height="${BLEED_HEIGHT}" fill="white"/>
        
        <!-- Card image with high quality preservation -->
        <image 
          width="${BLEED_WIDTH}" 
          height="${BLEED_HEIGHT}" 
          href="${imageUrl}" 
          preserveAspectRatio="xMidYMid meet"
          image-rendering="optimizeQuality" 
        />
      </svg>
    `;
  };

  // Generate a basic fallback preview when rendering fails
  const generateFallbackPreview = (side: 'front' | 'back'): string => {
    const bgColor = side === 'front' ? '#f8f9fa' : '#ffffff';
    const message = side === 'front' ? 'Card Front Preview' : 'Card Back Preview';
    
    const svgString = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
      <svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400">
        <rect width="600" height="400" fill="${bgColor}"/>
        <text x="300" y="200" font-family="Arial" font-size="24" fill="#adb5bd" text-anchor="middle">${message}</text>
      </svg>
    `;
    
    return svgToDataURL(svgString);
  };

  return (
    <div className={`space-y-4 ${className}`} id={id}>
      {/* Preview component */}
      <TemplatePreviewComponent 
        previewUrl={previewUrl}
        side={side}
        isLoading={isLoading}
        id={id ? `${id}-component` : undefined}
      />

      {/* Debug info component */}
      {showDebug && !isUserLoading && isAdmin && (
        <TemplateDebugInfo 
          debugInfo={debugInfo} 
          className="mt-4"
        />
      )}
    </div>
  );
} 